/// <amd-module name="Wit/AgileAnalytics/WorkItemRollupProvider" />

import { RollupCalculationSettings, RollupType } from "Analytics/Boards/RollupQueries/Contracts";
import { RollupQuery, RollupQueryResult, RollupQueryWorkItemResult, WorkItemsRecency } from "Analytics/Boards/RollupQueries/RollupQuery";
import { IObservableValue, ObservableValue } from "VSS/Core/Observable";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";

export interface IWorkItemRollupProvider {
    checkAndUpdateWorkItemIds(updatedWorkItemIds: number[]): number[];
    getRollupData(workItemId: number, interactive?: boolean): IObservableValue<RollupProgressBarData | undefined>;
    getRollupMetric(): RollupCalculationSettings;
    getWitRecencyData(workItemIds: number[]): Promise<WorkItemsRecency | undefined>;
    hasWorkItem(workItemId: number): boolean;
    isAnalyticsCaughtUp(axDates: WorkItemsRecency, opStoreDates: WorkItemsRecency): boolean;
    resetRollupData(workItemResetUtcTimestamp: number, workItemLinksResetUtcTimestamp: number): void;
}

export interface RollupProgressBarData {
    completedWorkCount: number;
    totalWorkCount: number;
    errorMessage?: string;
    rollupMetric?: RollupType;
}

interface OpStoreWorkItemsRecency {
    workItemsChangedDate?: Date;
    workItemLinksChangedDate?: Date;
}

export class WorkItemRollupProvider implements IWorkItemRollupProvider {
    /** Time to wait before determining the number of requests that were made to Analytics for a given grid load. */
    public static readonly debounceTimeRequestCountTelemetryMs = 1000;
    /** Time to wait betwen interactive data modifications before attempting to query Analytics */
    public static readonly debounceTimeGetDataMs = 10000;
    /** Time to wait between retries in case data in AX is bad quality (i.e. delayed) */
    public static readonly badQualityRetryMs = 10000;
    /** Number of retries to make when trying to retrieve quality data */
    public static readonly badQualityRetryAttempts = 4;

    /** How recent is analytics data? */
    public axRecency: WorkItemsRecency;

    private pageSize = 200;

    private workItemIdToRollupData: { [workItemId: number]: IObservableValue<RollupProgressBarData | undefined> } = {};
    private workItemIdToPageDataPromise: { [workItemId: number]: Promise<RollupQueryWorkItemResult[] | undefined> } = {};
    private interactiveMode = false;

    private rollupMetric: RollupCalculationSettings;
    private workItemIds: Set<number>;
    private backlogFilterWorkItemTypes?: string[];
    private pageContext: IVssPageContext;
    private projectId: string;

    // Global debouncing handler for this data provider
    private timerHandle: number;
    // Int for tracking the highest page index that has been requested in the current work item id set's ax request paging.
    private requestedPageIndicies: number[];
    // An array of work item IDs and waiting promise resolves. The array index is page number.
    // This is used to debounce requests to the page for interactive mode
    // For non-interactive we return the data promise directly, without waiting
    private resolvesByPage: {
        workItemIds: number[];
        resolves?: ((value?: RollupQueryWorkItemResult[] | PromiseLike<RollupQueryWorkItemResult[] | undefined>) => void)[];
    }[] = [];

    /** Internal tracking of op store recency information */
    private witRecency: WorkItemsRecency;

    private syncDatesPromise: Promise<RollupQueryWorkItemResult[] | undefined> | undefined;

    constructor(
        pageContext: IVssPageContext,
        rollupMetric: RollupCalculationSettings,
        projectId: string,
        backlogFilterWorkItemTypes?: string[]
    ) {
        this.pageContext = pageContext;
        this.projectId = projectId;
        this.rollupMetric = rollupMetric;
        this.backlogFilterWorkItemTypes = backlogFilterWorkItemTypes;

        this.requestedPageIndicies = [];
        this.resolvesByPage = [];

        this.workItemIds = new Set<number>();
        this.workItemIdToRollupData = {};
        this.workItemIdToPageDataPromise = {};
    }

    public checkAndUpdateWorkItemIds(updatedWorkItemIds: number[]): number[] {
        if (!updatedWorkItemIds) {
            return [];
        }

        // if more work item ids have been paged in, update with the new ids
        const workItemIdsToInitialize: number[] = [];
        updatedWorkItemIds.forEach(updatedWorkItemId => {
            if (!this.workItemIdToRollupData[updatedWorkItemId]) {
                workItemIdsToInitialize.push(updatedWorkItemId);
            }
        });

        this._initializeWorkItemIdMaps(workItemIdsToInitialize);
        updatedWorkItemIds.forEach(id => this.workItemIds.add(id));

        // return new workItemIds value for testing
        return [...this.workItemIds];
    }

    public resetWorkItemList(workItemIds: number[]): void {
        this.requestedPageIndicies = [];
        this.resolvesByPage = [];

        this.workItemIdToRollupData = {};
        this.workItemIdToPageDataPromise = {};

        this._initializeWorkItemIdMaps(workItemIds);
    }

    public resetRollupData(workItemResetUtcTimestamp: number, workItemLinksResetUtcTimestamp: number): void {
        this.workItemIdToRollupData = {};
        this.workItemIdToPageDataPromise = {};

        // If we are called, this means user was interacting with the page and needed to re-query the data
        // This controls debouncing - for initial load, there's none.
        this.interactiveMode = true;
        // Since user is interacting, we are also going to move WIT time forward
        if (!this.witRecency) {
            // If user started interacting before we got rollup data, we'll have to set this data initially
            this.witRecency = {};
        }
        if (workItemResetUtcTimestamp) {
            this.witRecency.workItemsChangedUtcTimestamp = workItemResetUtcTimestamp;
        }

        if (workItemLinksResetUtcTimestamp) {
            this.witRecency.workItemLinksChangedUtcTimestamp = workItemLinksResetUtcTimestamp;
        }

        // Kill a timer if there is one - we need to wait more for AX to catch up
        if (this.timerHandle) {
            clearTimeout(this.timerHandle);
        }

        this.requestedPageIndicies = [];
        this.syncDatesPromise = undefined;
    }

    public getRollupData(workItemId: number, interactive?: boolean): IObservableValue<RollupProgressBarData | undefined> {
        const rollupObservable = this.workItemIdToRollupData[workItemId];
        if (interactive) {
            this.interactiveMode = true;
        }

        // For temporary work items that were just created but not saved yet return empty data
        // Note: this will be re-queried from UI when item gets actual ID
        if (workItemId <= 0 || !rollupObservable) {
            return new ObservableValue({
                completedWorkCount: 0,
                totalWorkCount: 0,
                rollupMetric: this.getRollupMetric().type
            });
        }

        // have we retrieved this value or is its retrieval pending in a promise?
        if (!rollupObservable.value && !this.workItemIdToPageDataPromise[workItemId]) {
            this._loadPageData(workItemId)
                .then(rollupProgressData => {
                    if (rollupProgressData) {
                        rollupProgressData.forEach(rollupQueryResult => {
                            if (!this.workItemIdToRollupData[rollupQueryResult.WorkItemId]) {
                                this.workItemIdToRollupData[rollupQueryResult.WorkItemId] = new ObservableValue(
                                    this._interpretData(rollupQueryResult)
                                );
                            } else {
                                this.workItemIdToRollupData[rollupQueryResult.WorkItemId].value = this._interpretData(rollupQueryResult);
                            }
                        });
                    }
                })
                .catch(error => {
                    // Some analytics messages come back as error.error.message
                    let message = error.message;
                    if (error.error && error.error.message) {
                        message = error.error.message;
                    }
                    rollupObservable.value = {
                        completedWorkCount: 0,
                        totalWorkCount: 0,
                        errorMessage: message
                    };
                });
        }

        return this.workItemIdToRollupData[workItemId];
    }

    public getRollupMetric(): RollupCalculationSettings {
        return this.rollupMetric;
    }

    public hasWorkItem(workItemId: number): boolean {
        return this.workItemIds.has(workItemId);
    }

    private _interpretData(rollupQueryResult: RollupQueryWorkItemResult): RollupProgressBarData {
        const descendantDetails = rollupQueryResult.Descendants;
        let completedWorkCount = 0;
        let totalWorkCount = 0;
        descendantDetails.forEach(details => {
            if (details && (details.Count || details.Sum)) {
                if (details.isCompleted) {
                    completedWorkCount += details.Count || details.Sum || 0;
                }
                totalWorkCount += details.Count || details.Sum || 0;
            }
        });

        return {
            completedWorkCount: Math.round(completedWorkCount * 10) / 10,
            totalWorkCount: Math.round(totalWorkCount * 10) / 10,
            rollupMetric: this.getRollupMetric().type
        };
    }

    private _getPageIndex(workItemId: number): number {
        const workItemIndex = [...this.workItemIds].indexOf(workItemId);
        return Math.floor(workItemIndex / this.pageSize);
    }

    private _getPageInformation(workItemId: number): { pageIndex: number; workItemIds: number[] } {
        const pageIndex = this._getPageIndex(workItemId);
        const pageStartWorkItemIdIndex = pageIndex * this.pageSize;

        return {
            pageIndex: pageIndex,
            workItemIds: [...this.workItemIds].slice(pageStartWorkItemIdIndex, pageStartWorkItemIdIndex + this.pageSize)
        };
    }

    public async getWitRecencyData(workItemIds: number[]): Promise<WorkItemsRecency | undefined> {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const dataProviderId = "ms.vss-work-web.recency-workitem-data-provider";

        if (!this.witRecency) {
            const opStoreRecency = await contributionService.getDataAsync<OpStoreWorkItemsRecency>(dataProviderId);
            if (!opStoreRecency) {
                return;
            }

            // Note that op store and data provider returns data in UTC, although data provider parses it as local time.
            // We take timestamp anyways, so the time zone doesn't matter from here on
            const utcOpStoreDates: WorkItemsRecency = {
                workItemsChangedUtcTimestamp: opStoreRecency.workItemsChangedDate ? opStoreRecency.workItemsChangedDate.getTime() : undefined,
                workItemLinksChangedUtcTimestamp: opStoreRecency.workItemLinksChangedDate
                    ? opStoreRecency.workItemLinksChangedDate.getTime()
                    : undefined
            };

            this.witRecency = utcOpStoreDates;
        }

        return this.witRecency;
    }

    public isAnalyticsCaughtUp(axDates: WorkItemsRecency, opStoreDates: WorkItemsRecency): boolean {
        // Check that there's a match in data - if one of dates is null, the other one should also be null
        if (
            (!axDates.workItemLinksChangedUtcTimestamp && opStoreDates.workItemLinksChangedUtcTimestamp) ||
            (axDates.workItemLinksChangedUtcTimestamp && !opStoreDates.workItemLinksChangedUtcTimestamp) ||
            (!axDates.workItemsChangedUtcTimestamp && opStoreDates.workItemsChangedUtcTimestamp) ||
            (axDates.workItemsChangedUtcTimestamp && !opStoreDates.workItemsChangedUtcTimestamp)
        ) {
            return false;
        }

        // Check that AX is ahead, or both op store and AX have no data
        return (
            (axDates.workItemLinksChangedUtcTimestamp! >= opStoreDates.workItemLinksChangedUtcTimestamp! ||
                (!axDates.workItemLinksChangedUtcTimestamp && !opStoreDates.workItemLinksChangedUtcTimestamp)) &&
            // Check that AX is ahead, or both op store and AX have no data
            (axDates.workItemsChangedUtcTimestamp! >= opStoreDates.workItemsChangedUtcTimestamp! ||
                (!axDates.workItemsChangedUtcTimestamp && !opStoreDates.workItemsChangedUtcTimestamp))
        );
    }

    /** Start delayed query, exposed as public for stubbing */
    public runQueryDelayed(
        pageWorkItemIds: number[],
        retriesMade: number,
        initialStartTime: number
    ): Promise<RollupQueryWorkItemResult[] | undefined> {
        return new Promise<RollupQueryWorkItemResult[] | undefined>(resolve => {
            setTimeout(() => {
                resolve(this.runQuery(pageWorkItemIds, retriesMade + 1, initialStartTime, true));
            }, WorkItemRollupProvider.badQualityRetryMs);
        });
    }

    /** Common query runner
     * Start query for a set of work items and store the resulting promise in workItemIdToPageDataPromise map
     */
    private async runQuery(
        pageWorkItemIds: number[],
        retriesMade: number,
        initialStartTime?: number,
        isRetry?: boolean
    ): Promise<RollupQueryWorkItemResult[] | undefined> {
        const witRecencyQuery = this.getWitRecencyData(pageWorkItemIds);
        const startTime = Date.now();
        const pageContext = this.pageContext;
        const dataPromise = witRecencyQuery
            .then(async witRecency => {
                if (!witRecency) {
                    return;
                }

                const rollupQuery = new RollupQuery(pageWorkItemIds, this.rollupMetric, this.projectId, this.backlogFilterWorkItemTypes, [
                    {
                        providerTableName: "WorkItemRevision",
                        ExpectedSyncDate: new Date(witRecency.workItemsChangedUtcTimestamp || new Date())
                    },
                    {
                        providerTableName: "WorkItemLink",
                        ExpectedSyncDate: new Date(witRecency.workItemLinksChangedUtcTimestamp || new Date())
                    }
                ]);

                const processQueryResults = async (rollupData: RollupQueryResult, enableRetry: boolean) => {
                    if (this.isAnalyticsCaughtUp(rollupData.syncDates, witRecency)) {
                        this.axRecency = rollupData.syncDates;
                        return rollupData.results;
                    } else {
                        if (retriesMade < WorkItemRollupProvider.badQualityRetryAttempts && enableRetry) {
                            // Recursively call ourselves every 10 seconds and see if we can get updated data
                            // This attempts to see if better quality data is available, and then pulls data if it is
                            return this.runQueryDelayed(pageWorkItemIds, retriesMade, initialStartTime || startTime);
                        } else {
                            this.axRecency = rollupData.syncDates;
                            return undefined;
                        }
                    }
                };

                if (isRetry) {
                    const rollupData = await rollupQuery.runQuery(pageContext);
                    return processQueryResults(rollupData, true);
                }

                if (!this.syncDatesPromise) {
                    const rollupData = await rollupQuery.runQuery(pageContext);
                    this.syncDatesPromise = processQueryResults(rollupData, true);
                    return this.syncDatesPromise;
                } else {
                    // need to process these in order
                    await this.syncDatesPromise;
                    const rollupData = await rollupQuery.runQuery(pageContext);
                    return processQueryResults(rollupData, false);
                }
            })
            .then(rollupData => {
                pageWorkItemIds.forEach(pageWorkItemId => {
                    delete this.workItemIdToPageDataPromise[pageWorkItemId];
                });
                return rollupData;
            });

        pageWorkItemIds.forEach(pageWorkItemId => {
            // When this gets called via recursion this will replace first promise with most recent one
            // so that new calls to getRollupData will receive the last data promise made
            // but previous calls will wait for the chain constructed in the above setTimeout call
            this.workItemIdToPageDataPromise[pageWorkItemId] = dataPromise;
        });

        return dataPromise;
    }

    private _loadPageData(workItemId: number): Promise<RollupQueryWorkItemResult[] | undefined> {
        const pageInformation = this._getPageInformation(workItemId);

        // If interactive flag is set, debounce all requests to a data page
        if (this.interactiveMode) {
            // Wait 10 seconds for AX to catch up
            const timer = setTimeout(() => {
                // For each page that's waiting, run the query and resolve the awaiters
                this.resolvesByPage.forEach(pageResolves => {
                    const result = this.runQuery(pageResolves.workItemIds, 0);
                    // Resolve each callback in resolves array
                    pageResolves.resolves?.forEach(async resolve => {
                        resolve(result);
                    });
                });
                this.resolvesByPage = [];
                this.timerHandle = 0;
            }, WorkItemRollupProvider.debounceTimeGetDataMs);

            if (this.timerHandle) {
                clearTimeout(this.timerHandle);
            }

            this.timerHandle = timer;

            // Return new promise for each call
            return new Promise<RollupQueryWorkItemResult[]>(resolve => {
                // The promise resolve callback is stored in resolvesByPage, which will be fulfilled above after setTimeout succeeeds
                if (!this.resolvesByPage[pageInformation.pageIndex]) {
                    this.resolvesByPage[pageInformation.pageIndex] = {
                        workItemIds: pageInformation.workItemIds,
                        resolves: []
                    };
                }

                this.resolvesByPage[pageInformation.pageIndex].resolves?.push(resolve);
            });
        } else {
            // If we dont receive a new index value request then let the first timer go forth.
            if (this.requestedPageIndicies.indexOf(pageInformation.pageIndex) === -1) {
                this.requestedPageIndicies.push(pageInformation.pageIndex);
                // We have a new index that was requested. debounce the timeout.
            }

            return this.runQuery(pageInformation.workItemIds, 0);
        }
    }

    private _initializeWorkItemIdMaps(workItemIds: number[]) {
        workItemIds.forEach(workItemId => {
            this.workItemIdToRollupData[workItemId] = new ObservableValue(undefined);
            delete this.workItemIdToPageDataPromise[workItemId];
        });
    }
}
